/**
 * useSnapGrid.js — Section 7
 *
 * All clip operations snap to quarter-note grid positions.
 * Quarter note = 0.25 bars.
 * Snap formula: snappedBar = Math.round(barPosition * 4) / 4
 *
 * Apply snap to:
 *   - clip placement (drag from library)
 *   - clip move (drag on grid)
 *   - slice point
 *   - paste position
 *   - duplicate position
 *
 * NO clip can ever exist at a non-quarter-note position.
 *
 * Visual grid reference:
 *   - Grid lines at every quarter-note position
 *   - Lighter lines for quarter notes
 *   - Stronger lines for bar boundaries
 *   - Strongest lines for every 4th bar (phrase)
 */

import { useCallback } from 'react';
import { GRID } from '@/utils/constants';

/**
 * Snap a bar position to the nearest quarter-note.
 * Examples: 3.37 → 3.25, 3.63 → 3.75, 4.0 → 4.0
 *
 * @param {number} barPosition - Raw bar position (float)
 * @returns {number} - Snapped bar position (multiple of 0.25)
 */
export function snapToGrid(barPosition) {
  return Math.round(barPosition * 4) / 4;
}

/**
 * Convert pixel X position on the grid to a bar position.
 *
 * @param {number} mouseX - Mouse X in pixels relative to grid left edge
 * @param {number} gridOffset - Pixel offset of the grid start (default 0)
 * @returns {number} - Bar position (float)
 */
export function pixelToBar(mouseX, gridOffset = 0) {
  return (mouseX - gridOffset) / GRID.PX_PER_BAR;
}

/**
 * Convert pixel X to a snapped bar position.
 *
 * @param {number} mouseX
 * @param {number} gridOffset
 * @returns {number} - Snapped bar position
 */
export function pixelToSnappedBar(mouseX, gridOffset = 0) {
  return snapToGrid(pixelToBar(mouseX, gridOffset));
}

/**
 * Convert a bar position to pixel X.
 *
 * @param {number} barPosition
 * @returns {number} - Pixel X
 */
export function barToPixel(barPosition) {
  return barPosition * GRID.PX_PER_BAR;
}

/**
 * Get the pixel width of one quarter note.
 * @returns {number}
 */
export function quarterNotePixels() {
  return GRID.PX_PER_BAR / 4;
}

/**
 * Snap a clip's start_bar to the grid.
 * Ensures no clip can ever exist at a non-quarter-note position.
 *
 * @param {Object} clip - Clip object with start_bar
 * @returns {Object} - New clip with snapped start_bar
 */
export function snapClip(clip) {
  return {
    ...clip,
    start_bar: snapToGrid(clip.start_bar),
  };
}

/**
 * Snap a slice point to the nearest quarter note.
 *
 * @param {number} barPosition - Where the user clicked to slice
 * @returns {number} - Snapped position
 */
export function snapSlicePoint(barPosition) {
  return snapToGrid(barPosition);
}

/**
 * React hook providing snap utility functions bound to the current grid.
 */
export function useSnapGrid() {
  const snap = useCallback((barPosition) => snapToGrid(barPosition), []);
  const toBar = useCallback((mouseX, gridOffset) => pixelToBar(mouseX, gridOffset), []);
  const toSnappedBar = useCallback((mouseX, gridOffset) => pixelToSnappedBar(mouseX, gridOffset), []);
  const toPixel = useCallback((barPosition) => barToPixel(barPosition), []);

  return {
    snap,
    toBar,
    toSnappedBar,
    toPixel,
    snapClip,
    snapSlicePoint,
    quarterNotePixels: quarterNotePixels(),
  };
}

export default useSnapGrid;
